/**
 * 通用控件。
 *
 * 内部实现已换成 Tailwind + Radix（shadcn 那一套），但**导出签名一个都没变** ——
 * 18 个页面不需要改一行调用代码就换了脸。迁移期这是刻意的：
 * 一次性改 18 个页面 + 组件层，出问题时无法二分定位到底是哪一层坏的。
 */

import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '../lib/api.ts';
import { formatMinor } from '../lib/money.ts';
import { shortId, statusLabel, statusTone, type Tone } from '../lib/format.ts';
import type { Mode } from '../lib/types.ts';
import { cn } from '@/lib/cn.ts';
import { Button as UiButton } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import {
  Select as RSelect, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select.tsx';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog.tsx';

export function Card({
  title, sub, actions, children, flush,
}: {
  title?: ReactNode | undefined;
  sub?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  children: ReactNode;
  flush?: boolean | undefined;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-rule bg-surface shadow-[0_1px_2px_rgb(20_25_34/0.05)]">
      {(title !== undefined || actions !== undefined) && (
        <header className="flex items-center gap-2.5 border-b border-rule bg-sunk/50 px-3.5 py-2.5">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            {title !== undefined && <div className="text-[13.5px] font-semibold text-ink">{title}</div>}
            {sub !== undefined && <div className="text-[12px] text-ink-faint">{sub}</div>}
          </div>
          {actions}
        </header>
      )}
      <div className={flush === true ? '' : 'p-3.5'}>{children}</div>
    </section>
  );
}

/*
 * 状态胶囊：**底色统一中性，语义交给圆点**。
 *
 * 一张交易表里绝大多数行是「已请款」这个常态。把常态染成绿色是把墨浪费在
 * 不需要注意的地方 —— 整张表都在喊，真正要人动手的那两行反而沉下去。
 * 所以只有 warn（要人动手）和 bad（失败）着色，其余靠圆点保住扫读能力。
 */
const DOT_TONE: Readonly<Record<Tone, string>> = {
  good: 'bg-good',
  info: 'bg-info',
  warn: 'bg-warn',
  bad: 'bg-bad',
  neutral: 'bg-ink-faint',
};

export function Badge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-px text-[11.5px] font-semibold leading-relaxed',
        tone === 'bad'
          ? 'border-bad/30 bg-bad-soft text-bad'
          : tone === 'warn'
            ? 'border-warn/30 bg-warn-soft text-warn'
            : tone === 'neutral'
              ? 'border-rule bg-sunk text-ink-muted'
              : 'border-rule bg-sunk text-ink',
      )}
    >
      <span className={cn('size-1.5 shrink-0 rounded-full', DOT_TONE[tone])} />
      {children}
    </span>
  );
}

/**
 * 状态徽标：文案与色调都从状态值推导，各页面不必各自维护一份映射。
 *
 * `domain` 只在状态值跨实体撞名、全局映射必然有一方是错的时候才传
 * （见 format.ts 的 DOMAIN_OVERRIDE）。不传就是原来的全局口径。
 */
export function StatusBadge({ status, domain }: { status: string; domain?: string }) {
  return <Badge tone={statusTone(status, domain)}>{statusLabel(status, domain)}</Badge>;
}

/**
 * test / live 徽标。
 *
 * live **不用红**：红是错误色，而运营 90% 的时间就在 live 上 ——
 * 常驻红等于警报常亮，真出事时那一抹红已经没有信号价值了。
 * 要被标记的反而是 test：它里面的钱是假的、成功是假的，
 * 真正危险的误判是「以为在 live 其实在 test」。
 * live 给实心墨色（像账簿上盖下去的一枚章），红留给 live 下写操作的确认弹窗。
 */
export function ModeBadge({ mode }: { mode: Mode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-2 py-px font-mono text-[11px] font-bold uppercase tracking-wider',
        mode === 'live'
          ? 'border border-ink bg-ink text-paper'
          : 'border border-dashed border-warn/50 bg-warn-soft text-warn',
      )}
    >
      {mode}
    </span>
  );
}

export function Button({
  children, onClick, variant, size, disabled, type, title,
}: {
  children: ReactNode;
  onClick?: (() => void) | undefined;
  variant?: 'primary' | 'danger' | 'ghost' | undefined;
  size?: 'sm' | undefined;
  disabled?: boolean | undefined;
  type?: 'button' | 'submit' | undefined;
  title?: string | undefined;
}) {
  return (
    <UiButton
      type={type ?? 'button'}
      variant={variant ?? 'default'}
      size={size ?? 'default'}
      disabled={disabled === true}
      {...(onClick === undefined ? {} : { onClick })}
      {...(title === undefined ? {} : { title })}
    >
      {children}
    </UiButton>
  );
}

export function Field({
  label, hint, children,
}: {
  label: ReactNode;
  hint?: ReactNode | undefined;
  children: ReactNode;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-[12px] font-medium text-ink-muted">{label}</span>
      {children}
      {hint !== undefined && <span className="text-[11.5px] leading-snug text-ink-faint">{hint}</span>}
    </label>
  );
}

/*
 * 下拉选择。
 *
 * ⚠️ 空串哨兵：旧的原生实现用 `<option value="">` 表示「不筛选」，
 * 而 **Radix Select 明确禁止空字符串作为 item 值**（它把空串保留给
 * 「清空选择」的语义，传了会直接抛错）。如果照搬，所有带「全部」选项的
 * 筛选器都会在运行时炸掉或静默失效。
 *
 * 所以这里在边界上做一次双向映射：对外仍然是空串（页面代码不用改），
 * 对内换成 ALL 这个不可能与真实取值冲突的哨兵。
 */
const ALL = '__all__';

export function Select({
  value, onChange, options, placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  options: readonly { readonly value: string; readonly label: string }[];
  placeholder?: string | undefined;
}) {
  return (
    <RSelect
      value={value === '' ? ALL : value}
      onValueChange={(next) => { onChange(next === ALL ? '' : next); }}
    >
      {/*
        min-w 不能省：筛选行是 flex，字段宽度由内容撑开。
        原生 <input> 有约 20 字符的固有宽度，而 Radix 触发器只有当前选中项那么宽 ——
        不给下限的话，「状态」下拉会缩成一小块，和旁边的输入框参差不齐。
      */}
      <SelectTrigger className="min-w-[150px]">
        <SelectValue placeholder={placeholder ?? '请选择'} />
      </SelectTrigger>
      <SelectContent>
        {placeholder !== undefined && <SelectItem value={ALL}>{placeholder}</SelectItem>}
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
        ))}
      </SelectContent>
    </RSelect>
  );
}

export function TextInput({
  value, onChange, placeholder, type, numeric, disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  type?: 'text' | 'password' | 'email' | 'datetime-local' | undefined;
  numeric?: boolean | undefined;
  disabled?: boolean | undefined;
}) {
  return (
    <Input
      type={type ?? 'text'}
      value={value}
      disabled={disabled === true}
      className={numeric === true ? 'text-right font-mono tabular-nums' : undefined}
      onChange={(e) => { onChange(e.target.value); }}
      {...(placeholder === undefined ? {} : { placeholder })}
    />
  );
}

/**
 * 金额。**永远右对齐 + 等宽数字**，币种作为弱化标签跟在后面。
 * 金额本身由 `formatMinor` 从最小单位串算出来，全程 bigint。
 */
export function Money({
  value, currency, tone, showCurrency = true,
}: {
  value: string | number | bigint | null | undefined;
  currency: string;
  tone?: 'good' | 'bad' | 'muted' | undefined;
  showCurrency?: boolean | undefined;
}) {
  let text: string;
  try {
    text = formatMinor(value, currency);
  } catch {
    // 未知币种：宁可显示原值也不猜小数位
    text = String(value ?? '—');
  }
  return (
    <span
      className={cn(
        'num',
        tone === 'good' ? 'text-credit' : tone === 'bad' ? 'text-debit' : tone === 'muted' ? 'text-ink-faint' : undefined,
      )}
    >
      {text}
      {showCurrency && <CurrencyTag>{currency}</CurrencyTag>}
    </span>
  );
}

function CurrencyTag({ children }: { children: ReactNode }) {
  return <span className="ml-1 font-sans text-[11px] font-semibold tracking-wide text-ink-faint">{children}</span>;
}

/**
 * 币种不确定时的金额。
 *
 * 有些金额字段**天然没有绑定币种**（通道限额、`currency IS NULL` 的费率规则…）。
 * 这时候**不能挑一个币种去格式化**：按 USD 的 2 位小数渲染一个其实是 JPY 的数，
 * 屏幕上会显示 `50.00` 而真实值是 5000 日元 —— 差 100 倍且完全看不出错。
 * 所以只有币种唯一确定时才格式化，否则原样显示最小单位整数并标注出来。
 */
export function MinorAmount({
  value, currencies,
}: {
  value: string | number | bigint | null | undefined;
  /** 该金额可能适用的币种。恰好一个才格式化。 */
  currencies: readonly string[];
}) {
  if (value === null || value === undefined) {
    return <span className="text-ink-faint">—</span>;
  }
  const only = currencies.length === 1 ? currencies[0] : undefined;
  if (only !== undefined) {
    return <Money value={value} currency={only} showCurrency={false} />;
  }
  return (
    <span className="num" title="该字段未绑定币种，按最小单位原样显示">
      {String(value)}
      <CurrencyTag>最小单位</CurrencyTag>
    </span>
  );
}

/** 可复制的 id。表格里显示缩写，点击复制全量。 */
export function CopyId({ id, full }: { id: string; full?: boolean | undefined }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => { setCopied(false); }, 1200);
    return () => { clearTimeout(timer); };
  }, [copied]);

  return (
    <button
      className="rounded px-1 py-px font-mono text-[0.92em] tabular-nums text-ink-muted transition-colors hover:bg-sunk hover:text-ink"
      title={`点击复制 ${id}`}
      onClick={() => {
        void navigator.clipboard.writeText(id).then(() => { setCopied(true); });
      }}
    >
      {copied ? '已复制' : full === true ? id : shortId(id)}
    </button>
  );
}

export function Metric({
  label, value, note, tone, currency,
}: {
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode | undefined;
  tone?: 'good' | 'bad' | 'warn' | undefined;
  currency?: string | undefined;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11.5px] font-medium tracking-wide text-ink-muted">{label}</span>
      {/*
        数值默认**不着色**：之前一排 KPI 四种颜色像红绿灯，而这些数在多数日子里
        只是「今天的数」。只保留 bad —— 它用在真正要立刻处理的位置（账不平、漂移），
        也正因为别的数字都不着色，它才亮得出来。
      */}
      <span
        className={cn(
          'text-[26px] font-semibold leading-tight tracking-tight',
          tone === 'bad' ? 'text-bad' : 'text-ink',
        )}
      >
        {value}
        {currency !== undefined && <CurrencyTag>{currency}</CurrencyTag>}
      </span>
      {note !== undefined && <span className="text-[11.5px] leading-snug text-ink-faint">{note}</span>}
    </div>
  );
}

export function Empty({ title, hint }: { title: string; hint?: ReactNode | undefined }) {
  return (
    <div className="flex flex-col items-center gap-1.5 px-4 py-12 text-center">
      <div className="text-[14px] font-semibold text-ink">{title}</div>
      {hint !== undefined && <div className="max-w-md text-[13px] leading-relaxed text-ink-faint">{hint}</div>}
    </div>
  );
}

export function Loading({ label = '加载中' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-10 text-[13px] text-ink-faint">
      <span className="size-3.5 animate-spin rounded-full border-2 border-rule border-t-brand" />
      {label}
    </div>
  );
}

/**
 * 错误展示。
 *
 * `request_id` 必须露出来 —— 它是把用户看到的现象和服务端日志对上的唯一线索。
 * `details` 里常带着后端算好的关键数字（可提额度、冲突的费率规则），
 * 那些比错误文案本身更有用，所以一并展开。
 */
export function ErrorBox({ error }: { error: unknown }) {
  if (error === null || error === undefined) return null;

  const isApi = error instanceof ApiError;
  const severe = isApi ? error.status >= 500 : true;

  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-lg border px-3.5 py-2.5',
        severe ? 'border-bad/30 bg-bad-soft text-bad' : 'border-warn/30 bg-warn-soft text-warn',
      )}
    >
      <span className="text-[13px] font-semibold">
        {isApi ? error.message : error instanceof Error ? error.message : String(error)}
      </span>
      {isApi && (
        <span className="font-mono text-[11.5px] opacity-80">
          {error.code}
          {error.requestId !== null && ` · ${error.requestId}`}
        </span>
      )}
      {isApi && error.details !== null && (
        <span className="break-all font-mono text-[11.5px] opacity-80">{JSON.stringify(error.details)}</span>
      )}
    </div>
  );
}

/**
 * 弹窗。
 *
 * 调用方一直是 `{open && <Modal onClose={…} />}` 这种条件渲染，
 * 所以这里恒为 open，把关闭交回给 onClose —— 换成 Radix 之后
 * 焦点陷阱、Esc、点遮罩关闭、滚动锁定、焦点归还都由它接管，
 * 原来那些是手写的（只做了 Esc）。
 */
export function Modal({
  title, children, onClose, footer, wide,
}: {
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode | undefined;
  wide?: boolean | undefined;
}) {
  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className={cn('max-h-[85vh] overflow-y-auto', wide === true && 'max-w-3xl')}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">对话框</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">{children}</div>
        {footer !== undefined && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

/** 键值详情表。传 `null` 的行会被跳过，方便按角色隐藏某几行。 */
export function DetailGrid({ rows }: { rows: readonly (readonly [ReactNode, ReactNode] | null)[] }) {
  return (
    <div className="grid grid-cols-[minmax(88px,auto)_1fr] items-baseline gap-x-4 gap-y-2 text-[13px]">
      {rows
        .filter((row): row is readonly [ReactNode, ReactNode] => row !== null)
        .map((row, index) => (
          <Fragment key={index}>
            <span className="text-ink-faint">{row[0]}</span>
            <span className="min-w-0 break-words text-ink">{row[1]}</span>
          </Fragment>
        ))}
    </div>
  );
}

/**
 * 弹窗内的分组页签。
 *
 * `dirty` 让「哪一组有未保存的改动」在切走之后仍然看得见 ——
 * 否则用户改了成本、切到凭证页、点保存，完全不知道自己一次提交了两组东西。
 */
export function Tabs<T extends string>({
  tabs, active, onChange, dirty,
}: {
  tabs: readonly { readonly key: T; readonly label: string }[];
  active: T;
  onChange: (key: T) => void;
  dirty?: Readonly<Partial<Record<T, boolean>>>;
}) {
  return (
    <div className="flex gap-0.5 border-b border-rule" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={active === tab.key}
          onClick={() => { onChange(tab.key); }}
          className={cn(
            'relative -mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] transition-colors',
            active === tab.key
              ? 'border-brand font-semibold text-ink'
              : 'border-transparent text-ink-muted hover:text-ink',
          )}
        >
          {tab.label}
          {dirty?.[tab.key] === true && <span className="size-1.5 rounded-full bg-warn" />}
        </button>
      ))}
    </div>
  );
}
